/* eslint-disable  */
import type { Address } from "viem"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenFalse, ShowWhenTrue } from "@/components/conditionals"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MessageBox } from "@/domains/common/components/message-box"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { BankIcon } from "@/domains/icons/huge-icons/monotone/bank"
import { unstakeLiquidityActionInteraction } from "@/domains/interactions"
import { trimEthAddress } from "@/domains/library"
import { LiquidityPageLayout } from "@/domains/liquidity-page/components/liquidity-page-layout"
import { LiquidityPageTabs } from "@/domains/liquidity-page/components/liquidity-page-tabs"
import { MinimalPoolInputCard } from "@/domains/minimal-pool/components/minimum-pool-input-card"
import { MinimalPoolInputGaugeBalance } from "@/domains/minimal-pool/components/minimum-pool-input-gauge-balance"
import { ClassicLiquidityPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/classic-liquidity-pages-complex-breadcrumb-application"
import { ClassicLiquiditySubItem } from "@/domains/navigation/subitems/classic-liquidity-subitem"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useMinimalPool } from "@/hooks/use-minimal-pool"
import useEventEmitter from "@/hooks/useEventEmitter"

const BREADCRUMB_ITEMS = [new ClassicLiquiditySubItem(), ClassicLiquidityPagesComplexBreadcrumbApplication]

export default function UnstakePage() {
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const { address: poolAddressParam } = useParams<{ address: string }>()
  const poolAddress = poolAddressParam ? (poolAddressParam as Address) : undefined
  const minimalPool = useMinimalPool(poolAddress)

  // Hook up the orchestrator to run the loadUserManageMinimalPoolInfo query
  useEffect(() => {
    if (!minimalPool || !scaleApp) return
    scaleApp.MinimalPools.Orchestrator.startUserManagePoolSync(minimalPool, 30_000)
    return () => {
      scaleApp.MinimalPools.Orchestrator.stopUserManagePoolSync()
    }
  }, [minimalPool, scaleApp])

  const [amount, setAmount] = useState<string>("0")
  const amountWei = minimalPool?.parseUnits(amount) ?? 0n
  const canInteract = amountWei > 0n && amountWei <= (minimalPool?.onChainUserGaugeBalance ?? 0n)

  const unstakeAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    if (!minimalPool || !chainConfig) {
      console.error("Missing required dependencies")
      return
    }

    const chainId = chainConfig.chainId
    const _result = await unstakeLiquidityActionInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      chainId,
      config,
      emitter,
      amountWei,
      minimalPool,
      onSuccess: () => {
        setAmount("0")
        scaleApp.MinimalPools.Orchestrator.userManagePoolQueryInstance?.forceExecute()
      },
    })
  }

  const title = minimalPool ? `Unstake ${minimalPool.displayName} Liquidity` : "Unstake Liquidity"
  const subTitle = minimalPool ? `from the ${minimalPool.displayName} gauge` : "from the gauge"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Unstake Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <LiquidityPageLayout minimalPool={minimalPool} page="unstake">
          <LiquidityPageTabs />
          <Card.Container className="border-p400-light bg-p400 flex w-full min-w-[320px] flex-col gap-6 rounded-lg border p-4 lg:p-6">
            <ShowWhenTrue when={!!minimalPool}>
              <Card.Header>
                <div className="flex w-full flex-col">
                  <Card.HeaderDescription>
                    Users staked in the {minimalPool?.displayName} are currently earning{" "}
                    <span className="text-t200">{minimalPool?.displayApr}</span> APR
                  </Card.HeaderDescription>
                </div>
              </Card.Header>

              {minimalPool !== undefined ? (
                <MinimalPoolInputCard
                  amount={amount}
                  balance={minimalPool?.onChainUserGaugeBalance ?? 0n}
                  minimalPool={minimalPool}
                  onAmountChange={setAmount}
                  balanceSlot={
                    <MinimalPoolInputGaugeBalance
                      pool={minimalPool}
                      label={<BankIcon size={14} />}
                      className="text-t200"
                    />
                  }
                />
              ) : null}

              <div className="flex w-full justify-end">
                <Button disabled={!canInteract} variant={"primary"} onClick={() => unstakeAction()} size="fit">
                  Unstake
                </Button>
              </div>
            </ShowWhenTrue>
            <ShowWhenFalse when={!!minimalPool}>
              <MessageBox variant={"error"}>
                <p>
                  Couldn't find pair with address {trimEthAddress(poolAddress ?? "")}, click{" "}
                  <Link to={"/liquidity"} className="underline">
                    here
                  </Link>{" "}
                  to go back to Liquidity.
                </p>
              </MessageBox>
            </ShowWhenFalse>
          </Card.Container>
        </LiquidityPageLayout>
      </PageContainer>
    </TooltipProvider>
  )
}
